import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import styles from './FoodPage.module.css';
import ExpandableSection from './utils/ExpandableSection';
import { getFoodComponentsData } from './utils/static-food-component-list';
import { getSpotlightData, getExtendedData } from './utils/static-micronutrient';
import { seoConfig } from '../../utils/seoConfig';
import { WeightCalculator, WeightCalculatorInputs } from '../weight/WeightCalculator';
import FoodTabs from './FoodTabs';
import DailyGoalSummaryCard from './DailyGoalSummaryCard';
import FoodLogSection from './FoodLogSection';
import FoodHistorySection from './FoodHistorySection';
import AddMealPanel from './AddMealPanel';
import MealDayDetailsPanel from './MealDayDetailsPanel';
import { useFoodData } from './useFoodData';
import { formatHumanFromYyyyMmDd, getLocalYyyyMmDd } from '../../utils/dateUtils';
import PageShell from '../layout/PageShell';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../i18n/provider';

const FoodPage: React.FC = () => {
  const { t } = useTranslation();
  const foodComponentsData = React.useMemo(() => getFoodComponentsData(t), [t]);
  const spotlightData = React.useMemo(() => getSpotlightData(t), [t]);
  const extendedData = React.useMemo(() => getExtendedData(t), [t]);
  const { isAuthenticated } = useAuth();
  const isPublicMode = !isAuthenticated;
  const [activeTab, setActiveTab] = useState<'info' | 'track'>('track');
  const {
    savedGoal,
    showCalculator,
    setShowCalculator,
    loading,

    showAddMealPanel,
    setShowAddMealPanel,
    mealText,
    setMealText,
    selectedMealDate,
    setSelectedMealDate,
    isAnalyzingMeal,
    analysisError,
    analysisFeedback,
    setAnalysisError,
    setAnalysisFeedback,
    analyzedCalories,
    analyzedProtein,
    analyzedCarbs,
    analyzedFats,
    setAnalyzedCalories,
    setAnalyzedProtein,
    setAnalyzedCarbs,
    setAnalyzedFats,

    meals,
    mealHistory,

    showHistoryDayPanel,
    setShowHistoryDayPanel,
    historyDayTitle,
    historyDayMeals,
    isLoadingHistoryDayMeals,

    handleSaveGoal,
    handleAnalyzeMeal,
    handleSaveMeal,
    openHistoryDay,
  } = useFoodData(activeTab, isPublicMode);

  return (
    <>
      <Helmet>
        <title>{seoConfig.food.title}</title>
        <meta name="description" content={seoConfig.food.description} />
        {seoConfig.food.keywords && <meta name="keywords" content={seoConfig.food.keywords} />}
        <meta property="og:title" content={seoConfig.food.title} />
        <meta property="og:description" content={seoConfig.food.description} />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content={seoConfig.food.title} />
        <meta name="twitter:description" content={seoConfig.food.description} />
        <meta name="twitter:card" content="summary" />
      </Helmet>
      <PageShell variant="page">
        <div className={styles.foodPage}>
          <div className={styles.container}>         
          <h1 className={styles.title}>{t('food.title')}</h1>
          <FoodTabs activeTab={activeTab} onChangeTab={setActiveTab} />

          {activeTab === 'info' && (
            <div>
              <ExpandableSection data={foodComponentsData} />
              <ExpandableSection data={spotlightData} />
              <ExpandableSection data={extendedData} />
              <WeightCalculator />
            </div>
          )}

          {activeTab === 'track' && (
            <div>
              {loading ? (
                <div style={{ textAlign: 'center', padding: 32, color: '#888' }}>
                  {t('food.loading')}
                </div>
              ) : !savedGoal ? (
                <div>
                  <h2 style={{ color: 'var(--color-primary, #4CAF50)', marginBottom: 16 }}>{t('food.setGoal')}</h2>
                  {isPublicMode && (
                    <div style={{ marginBottom: 12, color: '#bbb' }}>
                      {t('food.anonymousDisabled')}
                    </div>
                  )}
                  <WeightCalculator onSave={handleSaveGoal} saveButtonDisabled={isPublicMode} />
                </div>
              ) : (
                <div>
                  <DailyGoalSummaryCard
                    goal={savedGoal}
                    showCalculator={showCalculator}
                    onToggle={() => setShowCalculator(!showCalculator)}
                    disabled={isPublicMode}
                  />

                  {showCalculator && (
                    <div style={{ marginTop: 20 }}>
                      <WeightCalculator
                        onSave={handleSaveGoal}
                        saveButtonDisabled={isPublicMode}
                        initialInputs={{
                          currentWeight: savedGoal.currentWeight,
                          targetWeight: savedGoal.targetWeight,
                          height: savedGoal.height,
                          age: savedGoal.age,
                          gender: savedGoal.gender,
                          timeframeMonths: savedGoal.timeframeMonths,
                          activityLevel: savedGoal.activityLevel,
                        } satisfies Partial<WeightCalculatorInputs>}
                      />
                    </div>
                  )}

                  <FoodLogSection
                    title={`${t('food.todayFoodLog')} • ${formatHumanFromYyyyMmDd(getLocalYyyyMmDd())}`}
                    meals={meals}
                    goalCalories={savedGoal.dailyCalories}
                    goalProtein={savedGoal.dailyProtein}
                    goalCarbs={savedGoal.dailyCarbs}
                    goalFats={savedGoal.dailyFats}
                  />

                  <FoodHistorySection
                    goalCalories={savedGoal.dailyCalories}
                    days={mealHistory
                      .filter((d) => d.mealDate !== getLocalYyyyMmDd())
                      .slice(0, 7)
                      .map((d) => {
                        const dateKey = d.mealDate;
                        const title = formatHumanFromYyyyMmDd(dateKey);
                        return {
                          title,
                          date: dateKey,
                          metGoal: d.calories >= savedGoal.dailyCalories,
                          calories: d.calories,
                          protein: d.protein,
                          carbs: d.carbs,
                          fats: d.fats,
                        };
                      })}
                    onSelectDay={openHistoryDay}
                  />
                </div>
              )}
            </div>
          )}

          {activeTab === 'track' && savedGoal && (
            <button
              type="button"
              className="fab"
              onClick={() => setShowAddMealPanel(true)}
              disabled={isPublicMode}
              style={{ bottom: 84 }}
            >
              +
            </button>
          )}

          <AddMealPanel
            open={showAddMealPanel}
            selectedDate={selectedMealDate}
            onChangeDate={setSelectedMealDate}
            mealText={mealText}
            onChangeMealText={setMealText}
            isAnalyzing={isAnalyzingMeal}
            error={analysisError}
            feedback={analysisFeedback}
            analyzedCalories={analyzedCalories}
            analyzedProtein={analyzedProtein}
            analyzedCarbs={analyzedCarbs}
            analyzedFats={analyzedFats}
            onChangeCalories={setAnalyzedCalories}
            onChangeProtein={setAnalyzedProtein}
            onChangeCarbs={setAnalyzedCarbs}
            onChangeFats={setAnalyzedFats}
            onClose={() => {
              setShowAddMealPanel(false);
              setAnalysisFeedback(null);
              setAnalysisError(null);
            }}
            onAnalyze={handleAnalyzeMeal}
            onSave={handleSaveMeal}
            disableActions={isPublicMode}
          />

          <MealDayDetailsPanel
            open={showHistoryDayPanel}
            date={historyDayTitle}
            meals={historyDayMeals}
            loading={isLoadingHistoryDayMeals}
            onClose={() => setShowHistoryDayPanel(false)}
          />
          </div>
        </div>
      </PageShell>
    </>
  );
};

export default FoodPage;
